/**
 * Create and initialise Tako card
 */

/* -----------------------------------------------------------------------------
 * Tako Card
 * -------------------------------------------------------------------------- */
import { getDirectChildren } from './utils/getDirectChildren';
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';

const COMPONENT_NAME = 'Card';

const cardInstances = new Map();

export const init = (unresolvedScope) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);
    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    /**
     * tako-card--collapsible class is @deprecated
     */
    const closableCardsWithLegacyClass = scope.querySelectorAll(
        '.tako-card--collapsible',
    );

    if (closableCardsWithLegacyClass.length) {
        logger.warn(
            'The `tako-card--collapsible` class is deprecated. Please use the `data-closable` attribute instead.',
            defaultLoggerArgs,
        );
    }

    const allClosableTakoCards = scope.querySelectorAll(
        '.tako-card[data-closable], .tako-card[data-open], .tako-card--collapsible',
    );

    allClosableTakoCards.forEach((takoCard) => {
        try {
            const instanceLoggerArgs = {
                ...defaultLoggerArgs,
                element: takoCard,
            };
            const openIconHTML =
                '<span class="tako-icon"><i class="far fa-angle-down" aria-hidden="true"></i></span>';
            const closeIconHTML =
                '<span class="tako-icon"><i class="far fa-angle-up" aria-hidden="true"></i></span>';
            const closeText = 'Close section';
            const openText = 'Open section';
            const cardHeader = getDirectChildren(
                takoCard,
                'tako-card__header',
            )[0];
            const cardContent = getDirectChildren(
                takoCard,
                'tako-card__content',
            );
            const DATA_OPEN = 'data-open';

            if (!cardHeader) {
                logger.warn(
                    'Collapsible Cards must have a card header - add one using the `tako-card__header` class',
                    instanceLoggerArgs,
                );
                return;
            }

            if (!cardContent.length) {
                logger.warn(
                    'Collapsible cards without a content class are deprecated. Please add the `tako-card__content` class to the card content.',
                    instanceLoggerArgs,
                );
            }

            // Set the attribute data-open to false if card content is hidden
            cardContent.forEach((item) => {
                if (window.getComputedStyle(item).display === 'none') {
                    item.closest('.tako-card').setAttribute(DATA_OPEN, false);
                }
            });

            // replace deprecated `tako-card--collapsed` class with `data-open="false"` attribute for closable cards
            if (takoCard.classList.contains('tako-card--collapsed')) {
                logger.warn(
                    'The `tako-card--collapsed` class is deprecated. Please use the `data-open` attribute instead.',
                    instanceLoggerArgs,
                );
                takoCard.setAttribute(DATA_OPEN, false);
            }

            // Set data attribute to open cards by default
            if (
                !takoCard.hasAttribute(DATA_OPEN) ||
                takoCard.getAttribute(DATA_OPEN) !== 'false'
            ) {
                takoCard.setAttribute(DATA_OPEN, true);
            }

            const isCardOpen = takoCard.getAttribute(DATA_OPEN) !== 'false';

            /**
             * tako-card__toggle-button class is @deprecated in favour of data-card-toggle attribute
             * leaving legacy class in case it's used in custom JS
             * ToDo: remove it with the next breaking change
             */
            const legacyCardToggleClass = 'tako-card__toggle-button';

            // Create a toggle button and add to the card header
            const toggleButton = document.createElement('button');
            toggleButton.classList.add(
                'tako-button',
                'tako-button--secondary',
                'tako-button--ghost',
                'tako-button--icon',
                legacyCardToggleClass,
            );
            toggleButton.setAttribute('type', 'button');
            toggleButton.setAttribute('data-card-toggle', true);
            toggleButton.setAttribute(
                'title',
                isCardOpen ? closeText : openText,
            );
            toggleButton.innerHTML = isCardOpen ? closeIconHTML : openIconHTML;

            const headerButtonGroup = getDirectChildren(
                cardHeader,
                'tako-button-group',
            )[0];

            const headerButtons = getDirectChildren(cardHeader, 'tako-button');

            if (headerButtonGroup) {
                headerButtonGroup.append(toggleButton);
            } else if (!headerButtonGroup && headerButtons.length) {
                // Wrap buttons in a tako-button-group div
                const newButtonGroup = document.createElement('div');
                newButtonGroup.classList.add('tako-button-group');
                cardHeader.append(newButtonGroup);
                headerButtons.forEach((button) => {
                    newButtonGroup.append(button);
                });
                newButtonGroup.append(toggleButton);
            } else {
                cardHeader.append(toggleButton);
            }

            const cardToggleButton =
                takoCard.querySelector('[data-card-toggle]');

            if (!cardToggleButton) {
                logger.warn(
                    'Card toggle button is not found',
                    instanceLoggerArgs,
                );
                return;
            }

            // Legacy code: Find first sibling of the card header to toggle visibility regardless of class
            const getFirstSiblingWithoutClass = () => {
                const firstSibling = cardHeader.nextElementSibling;
                return firstSibling &&
                    !firstSibling.classList.contains('tako-card__content')
                    ? firstSibling
                    : null;
            };
            const firstSiblingWithoutClass = getFirstSiblingWithoutClass();

            /**
             * @deprecated use the class tako-card__content to toggle card content instead
             */
            const openFirstSibling = () => {
                firstSiblingWithoutClass.style.display = '';
                firstSiblingWithoutClass.setAttribute('aria-hidden', false);
            };

            /**
             * @deprecated use the class tako-card__content to toggle card content instead
             */
            const closeFirstSibling = () => {
                firstSiblingWithoutClass.style.display = 'none';
                firstSiblingWithoutClass.setAttribute('aria-hidden', true);
            };

            if (!isCardOpen) {
                if (firstSiblingWithoutClass) {
                    firstSiblingWithoutClass.style.display = 'none';
                }
                cardContent.forEach((item) => (item.style.display = 'none'));
            }

            const setAriaAttributes = (isCardOpen) => {
                cardContent.forEach((item) =>
                    item.setAttribute('aria-hidden', !isCardOpen),
                );
                cardToggleButton.setAttribute('aria-expanded', isCardOpen);
            };

            setAriaAttributes(isCardOpen);

            const open = () => {
                // remove deprecated `tako-card--collapsed` class
                takoCard.classList.remove('tako-card--collapsed');
                takoCard.setAttribute(DATA_OPEN, true);

                cardContent.forEach((item) => {
                    // Reset the original display value
                    item.style.display = '';
                    // Force visibility if card content is hidden by a utility class or custom styles
                    if (window.getComputedStyle(item).display === 'none') {
                        item.style.display = 'block';
                    }
                });

                cardToggleButton.innerHTML = closeIconHTML;
                cardToggleButton.setAttribute('title', closeText);
                setAriaAttributes(true);
                if (firstSiblingWithoutClass) {
                    openFirstSibling();
                }

                // Emit a custom event when the card is opened
                const openEvent = new CustomEvent('takoCardOpen', {
                    detail: {
                        takoCard,
                        cardToggleButton,
                        id: takoCard.id,
                    },
                });
                takoCard.dispatchEvent(openEvent);
            };

            const close = () => {
                // add deprecated `tako-card--collapsed` class
                takoCard.classList.add('tako-card--collapsed');
                takoCard.setAttribute(DATA_OPEN, false);

                cardContent.forEach((item) => (item.style.display = 'none'));

                cardToggleButton.innerHTML = openIconHTML;
                cardToggleButton.setAttribute('title', openText);
                setAriaAttributes(false);
                if (firstSiblingWithoutClass) {
                    closeFirstSibling();
                }

                // Emit a custom event when the card is closed
                const closeEvent = new CustomEvent('takoCardClose', {
                    detail: {
                        takoCard,
                        cardToggleButton,
                        id: takoCard.id,
                    },
                });
                takoCard.dispatchEvent(closeEvent);
            };

            // Handle toggle button click events
            const handleClick = () => {
                if (takoCard.getAttribute(DATA_OPEN) !== 'false') {
                    close();
                } else {
                    open();
                }
            };

            cardToggleButton.addEventListener('click', handleClick);

            // Store the instance for external control via `toggle`
            cardInstances.set(takoCard, { open, close });
        } catch (error) {
            logger.error(error, {
                ...defaultLoggerArgs,
                element: takoCard,
            });
        }
    });
};

/**
 * Tako Card Functions
 */

const debounceDelay = 100;
const debounceTimeouts = new Map();

/**
 * Toggles the visibility of one or more Tako Card components.
 *
 * @param {string} selector - Document query selector (id or class) to target the card(s).
 *                            Accepts an id (e.g., '#my-card') or a class (e.g., '.my-card-class').
 * @param {boolean|null} [open=null] - Optional. If `true`, explicitly open the card(s).
 *                                     If `false`, explicitly close the card(s).
 *                                     If `null` (default), the card's current state will be toggled.
 *
 * @example
 * // Toggle based on the current state (open if closed, close if open):
 * toggleCard('.my-card-class');
 *
 * @example
 * // Explicitly open the card(s):
 * toggleCard('#my-card-id', true);
 *
 * @example
 * // Explicitly close the card(s):
 * toggleCard('.my-card-class', false);
 */
export const toggle = (selector, open = null) => {
    const logger = getLogger();

    // Support id's without the # prefix
    if (selector[0] !== '#' && selector[0] !== '.') {
        selector = `#${selector}`;
    }

    // Get all elements that match the selector (could be multiple if class selector is used)
    const takoCardElements = document.querySelectorAll(selector);

    if (!takoCardElements.length) {
        logger.warn(
            `No Tako Card components found with the selector "${selector}".`,
        );
        return;
    }

    takoCardElements.forEach((takoCardElement) => {
        const instance = cardInstances.get(takoCardElement);

        if (!instance) {
            logger.warn(
                `No instance found for the element with the selector "${selector}". Make sure the Tako card has been initialized.`,
            );
            return;
        }

        // Clear any existing timeout for this card
        if (debounceTimeouts.has(takoCardElement)) {
            clearTimeout(debounceTimeouts.get(takoCardElement));
        }

        const shouldOpen =
            open !== null
                ? open
                : takoCardElement.getAttribute('data-open') === 'false'; // If `open` is null, toggle based on current state

        // Handle debounce delay to ensure proper timing
        debounceTimeouts.set(
            takoCardElement,
            setTimeout(() => {
                if (shouldOpen) {
                    instance.open();
                } else {
                    instance.close();
                }

                // Remove the timeout once it's executed
                debounceTimeouts.delete(takoCardElement);
            }, debounceDelay),
        );
    });
};
